/* La librería de Excel pesa ~400 kB. Se carga solo cuando hace falta,
   así el arranque de la aplicación no la arrastra. */
const cargarXLSX = () => import('xlsx');
import { render } from '../core/app.js';
import { cifra } from '../core/componentes.js';
import { contratoDe, nivel } from '../core/dominio.js';
import { S } from '../core/estado.js';
import { MEDIDAS, esc, eur, num } from '../core/formato.js';
import { toast } from '../core/ui.js';
import { db } from '../data/db.js';

/* =====================================================================
   IMPORTACIÓN DEL CUADRO DE PRECIOS DESDE EXCEL
   ===================================================================== */

/* Quita acentos, signos y mayúsculas para comparar encabezados sin sustos */
export const llave = s => String(s??'').toLowerCase().normalize('NFD')
  .replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');

/* Sinónimos admitidos en la fila de encabezado */
export const COLUMNAS = {
  codigo: ['codigo','cod','codigounidad','codunidad','ref','referencia','clave'],
  desc:   ['unidad','unidaddeobra','descripcion','desc','concepto','denominacion','designacion','definicion'],
  ud:     ['unidaddemedida','medida','ud','uds','unidadmedida','umedida','um'],
  precio: ['precio','preciounitario','preciound','importe','importeunitario','pu','euros','eur'],
  capitulo: ['capitulo','seccion','grupo']
};

export function mapearColumnas(cabecera){
  const mapa = {};
  cabecera.forEach((celda,i)=>{
    const k = llave(celda);
    if(!k) return;
    for(const campo in COLUMNAS){
      if(mapa[campo]!==undefined) continue;
      if(COLUMNAS[campo].includes(k)){ mapa[campo]=i; return; }
    }
  });
  /* Segunda pasada más permisiva: "precio (€)" o "descripción de la unidad" */
  cabecera.forEach((celda,i)=>{
    const k = llave(celda);
    if(!k || Object.values(mapa).includes(i)) return;
    for(const campo in COLUMNAS){
      if(mapa[campo]!==undefined) continue;
      if(COLUMNAS[campo].some(alias=>k.startsWith(alias))){ mapa[campo]=i; return; }
    }
  });
  return mapa;
}

/* Un precio puede venir como número, "1.234,56", "1,234.56", "12,40 €"... */
export function leerPrecio(v){
  if(typeof v === 'number') return isFinite(v) ? v : NaN;
  let s = String(v??'').replace(/[€\s\u00a0]/g,'').trim();
  if(!s) return NaN;
  const coma = s.lastIndexOf(','), punto = s.lastIndexOf('.');
  if(coma>-1 && punto>-1){
    /* El último separador que aparece es el decimal */
    s = coma>punto ? s.replace(/\./g,'').replace(',','.') : s.replace(/,/g,'');
  } else if(coma>-1){
    /* Coma sola: decimal salvo que sea separador de miles (1,234) */
    s = /,\d{3}$/.test(s) && s.replace(/[^\d]/g,'').length>3 ? s.replace(',','') : s.replace(',','.');
  } else if(punto>-1){
    /* Punto solo y tres cifras detrás: en un Excel español eso son miles (1.500),
       salvo que la parte entera sea 0, donde sí es decimal (0.021) */
    if(/^\d*\.\d{3}$/.test(s) && !/^0\./.test(s)) s = s.replace(/\./g,'');
    else if((s.match(/\./g)||[]).length>1) s = s.replace(/\./g,'');
  }
  const n = parseFloat(s);
  return isFinite(n) ? n : NaN;
}

export function leerMedida(v){
  const s = String(v??'').trim();
  if(!s) return null;
  if(MEDIDAS.includes(s)) return s;
  /* Los superíndices los pierde llave(), así que los bajamos a cifra antes */
  const plano = k => llave(String(k).replace(/²/g,'2').replace(/³/g,'3'));
  const equiv = {m2:'m²', m3:'m³', metros:'m', metro:'m', ml:'m', metrolineal:'m', metroslineales:'m',
                 u:'ud', uds:'ud', und:'ud', unid:'ud', unidad:'ud', unidades:'ud',
                 hora:'h', horas:'h', hr:'h', tm:'t', tn:'t', tonelada:'t', toneladas:'t',
                 kilogramo:'kg', kilogramos:'kg', kilo:'kg', kilos:'kg', litro:'l', litros:'l',
                 kilometro:'km', kilometros:'km', kms:'km', porcentaje:'%', pct:'%',
                 jornadas:'jornada',
                 m2lineales:'m²', metrocuadrado:'m²', metroscuadrados:'m²',
                 metrocubico:'m³', metroscubicos:'m³'};
  const p = plano(s);
  const directo = MEDIDAS.find(m=>plano(m)===p);
  if(directo) return directo;
  return equiv[p] || null;
}

/* Convierte la hoja en filas clasificadas: nueva, actualiza o error */
export function analizarFilas(matriz, cid){
  if(!matriz.length) return {error:'La hoja está vacía.'};

  /* La cabecera puede no ser la fila 1: buscamos la primera que reconozcamos */
  let iCab = -1, mapa = null;
  for(let i=0; i<Math.min(matriz.length,15); i++){
    const m = mapearColumnas(matriz[i]||[]);
    if(m.codigo!==undefined && m.desc!==undefined && m.precio!==undefined){ iCab=i; mapa=m; break; }
  }
  if(iCab===-1) return {error:'No he encontrado la fila de encabezado. Tiene que haber una fila con las columnas Código, Unidad, Unidad de medida y Precio.'};

  const existentes = db.precios.filter(p=>p.contrato===cid);
  const vistos = new Map();
  const filas = [];

  for(let i=iCab+1; i<matriz.length; i++){
    const f = matriz[i] || [];
    const codigo = String(f[mapa.codigo]??'').trim();
    const desc   = String(f[mapa.desc]??'').trim();
    const precio = leerPrecio(f[mapa.precio]);
    const udCruda= mapa.ud!==undefined ? f[mapa.ud] : '';
    const ud     = leerMedida(udCruda);
    const capitulo = mapa.capitulo!==undefined ? String(f[mapa.capitulo]??'').trim() : '';

    if(!codigo && !desc && !isFinite(precio)) continue;   /* fila en blanco */

    const fallos = [];
    if(!codigo) fallos.push('falta el código');
    if(!desc)   fallos.push('falta la descripción');
    if(!isFinite(precio)) fallos.push('el precio no es un número');
    else if(precio<=0)    fallos.push('el precio tiene que ser mayor que cero');
    if(mapa.ud===undefined) fallos.push('falta la columna de unidad de medida');
    else if(!ud) fallos.push(`«${String(udCruda??'').trim()||'vacío'}» no es una unidad de medida válida`);

    const clave = codigo.toUpperCase();
    if(codigo && vistos.has(clave)) fallos.push(`el código está repetido en la fila ${vistos.get(clave)} del Excel`);
    else if(codigo) vistos.set(clave, i+1);

    const yaExiste = existentes.find(p=>p.codigo.toUpperCase()===clave);
    filas.push({
      linea: i+1, codigo, desc, ud, precio, capitulo,
      estado: fallos.length ? 'mal' : (yaExiste ? 'actu' : 'nueva'),
      fallos, anterior: yaExiste || null, idExistente: yaExiste?.id ?? null
    });
  }

  if(!filas.length) return {error:'He encontrado el encabezado, pero no hay ninguna fila con datos debajo.'};
  return {filas, sinColumnaUd: mapa.ud===undefined};
}

export function importarExcel(file, cid){
  const nombre = file.name;
  const lector = new FileReader();
  lector.onerror = ()=> toast('No he podido leer el fichero');
  lector.onload = async ev => {
    let matriz, XLSX;
    try{
      XLSX = await cargarXLSX();
    }catch(err){
      toast('No he podido cargar el lector de Excel');
      return;
    }
    try{
      /* Siempre Uint8Array: pasar el ArrayBuffer pelado da problemas según el navegador */
      const libro = XLSX.read(new Uint8Array(ev.target.result), {type:'array'});
      const hoja  = libro.Sheets[libro.SheetNames[0]];
      matriz = XLSX.utils.sheet_to_json(hoja, {header:1, raw:true, defval:''});
    }catch(err){
      toast('El fichero no parece un Excel válido');
      return;
    }
    const r = analizarFilas(matriz, cid);
    if(r.error){ toast(r.error); return; }
    S.modal = {t:'importar', c:cid, nombre, filas:r.filas, pisar:true};
    render();
  };
  lector.readAsArrayBuffer(file);
}

export function modalImportar(cid){
  const m = S.modal, c = contratoDe(cid);
  const nuevas = m.filas.filter(f=>f.estado==='nueva');
  const actu   = m.filas.filter(f=>f.estado==='actu');
  const malas  = m.filas.filter(f=>f.estado==='mal');
  const aplicar = nuevas.length + (m.pisar ? actu.length : 0);
  const importe = f => `${num(f.precio)} € / ${esc(f.ud||'?')}`;

  return `<div class="modal-bg" data-act="cerrar-fuera"><div class="modal modal-ancho">
    <div class="modal-h"><div><h3>Vista previa de la importación</h3>
      <div class="sub" style="font-size:12.5px;color:var(--gris)">${esc(m.nombre)} → ${esc(c.expediente)}</div></div>
      <button class="cerrar" data-act="cerrar" aria-label="Cerrar">×</button></div>
    <div class="modal-b">
      <div class="grid g3" style="margin-bottom:16px">
        <div class="cifra"><div class="l">Unidades nuevas</div><div class="v v-verde">${nuevas.length}</div></div>
        <div class="cifra"><div class="l">Ya existentes</div><div class="v ${actu.length?'v-ambar':''}">${actu.length}</div></div>
        <div class="cifra"><div class="l">Con errores</div><div class="v ${malas.length?'v-rojo':''}">${malas.length}</div></div>
      </div>

      ${actu.length?`<div class="aviso a-ambar"><div class="txt">
        <b>${actu.length} código${actu.length>1?'s ya están':' ya está'} en el cuadro de precios</b>
        <label style="display:flex;gap:8px;align-items:flex-start;margin-top:7px;cursor:pointer">
          <input type="checkbox" id="imp-pisar" data-act="alternar-pisar" ${m.pisar?'checked':''} style="margin-top:2px">
          <span>Actualizar su descripción y su precio con lo que trae el Excel. Si lo dejas sin marcar se quedan como están.</span></label>
        <div style="margin-top:6px;font-size:12px">Las imputaciones ya registradas conservan el importe con el que se hicieron: cambiar el precio solo afecta a lo que se registre a partir de ahora.</div>
      </div></div>`:''}

      ${malas.length?`<div class="aviso a-rojo"><div class="txt">
        <b>${malas.length} fila${malas.length>1?'s se descartan':' se descarta'}</b>
        Se importa todo lo demás. Corrige esas filas en el Excel y vuelve a subirlo cuando quieras: los códigos que ya hayan entrado aparecerán como existentes.</div></div>`:''}

      <div class="tabla-wrap" style="max-height:340px;overflow:auto"><table class="imp-r">
        <thead><tr><th>Fila</th><th>Código</th><th>Unidad de obra</th><th class="num">Precio</th><th>Estado</th></tr></thead>
        <tbody>${m.filas.map(f=>`<tr class="imp-${f.estado}">
          <td style="color:var(--gris-claro)">${f.linea}</td>
          <td class="cod">${esc(f.codigo)||'—'}</td>
          <td>${esc(f.desc)||'—'}</td>
          <td class="num">${f.estado==='mal'&&!isFinite(f.precio)?'—':importe(f)}
            ${f.estado==='actu'&&f.anterior&&Math.abs(f.anterior.precio-f.precio)>0.0001
              ? `<div style="font-size:11px;color:var(--gris-claro);text-decoration:line-through">${num(f.anterior.precio)} €</div>`:''}</td>
          <td>${f.estado==='nueva'?'<span class="et-imp et-nueva">Nueva</span>'
              : f.estado==='actu'?'<span class="et-imp et-actu">Ya existe</span>'
              : `<span class="et-imp et-mal">Error</span><div style="font-size:11px;margin-top:3px">${esc(f.fallos.join('; '))}</div>`}</td>
        </tr>`).join('')}</tbody></table></div>
    </div>
    <div class="modal-f"><button class="btn" data-act="cerrar">Cancelar</button>
      <button class="btn btn-p" data-act="confirmar-import" data-c="${cid}" ${aplicar?'':'disabled'}>
        ${aplicar?`Importar ${aplicar} unidad${aplicar>1?'es':''}`:'No hay nada que importar'}</button></div></div></div>`;
}

export async function descargarPlantilla(){
  const filas = [
    ['Código','Unidad','Unidad de medida','Precio'],
    ['SV.01','Señal triangular 900 mm reflectante nivel 2, incluso poste y colocación','ud',128.40],
    ['SV.02','Cartel flecha 1200 x 300 mm en lama de aluminio','ud',96.75],
    ['MM.01','Desbroce mecánico de márgenes con brazo desbrozador','m²',0.14],
    ['MM.02','Aplicación de herbicida homologado','m²',0.21],
    ['PI.01','Poda de arbolado viario con cesta elevadora, incluso retirada de restos','h',28.50]
  ];
  try{
    const XLSX = await cargarXLSX();
    const hoja = XLSX.utils.aoa_to_sheet(filas);
    hoja['!cols'] = [{wch:12},{wch:62},{wch:18},{wch:12}];
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Cuadro de precios');
    XLSX.writeFile(libro, 'GesCon_plantilla_cuadro_de_precios.xlsx');
    toast('Plantilla descargada. Borra las filas de ejemplo y mete las tuyas.');
  }catch(err){
    toast('No he podido generar la plantilla.');
  }
}

export function conectarSubidaExcel(){
  document.getElementById('subida-excel').addEventListener('change', e=>{
    const f = e.target.files[0];
    const cid = +e.target.dataset.c;
    e.target.value = '';
    if(f) importarExcel(f, cid);
  });
}

